import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { AiCoachTurnOption, AiCoachTurnOptionClientAction, AiCoachTurnPhase } from 'api/AICoachConversationApi';
import { closeAIChat } from 'handlers/aiChat';
import { AiCoachConversationEntry, addAiCoachUserChoice, clearAiCoachClientAction } from 'handlers/aiCoachConversation';
import { RoutePath } from 'enums/Routes';
import { useNavigate } from 'hooks/useNavigate';
import { getAiCoachConversation } from 'selectors/getAiCoachConversation';
import { getApplicationData } from 'selectors/getApplicationData';
import { getAICoachGoals, getGoalProgress, getNextSteps, postAiCoachConversation } from 'thunks';
import { OFFER_AVAILABLE_FROM_DASHBOARD_STATE } from 'utils/navigationStates';
import { showIntercom } from 'utils/intercom';

const CLIENT_ACTION_DELAY_MS = 1000;

export interface UseAiCoachConversationResult {
  entries: AiCoachConversationEntry[];
  options: AiCoachTurnOption[];
  /** True once the conversation has ended. */
  terminal: boolean;
  loading: boolean;
  error: boolean;
  phase?: AiCoachTurnPhase;
  selectOption: (optionId: string) => void;
  retry: () => void;
}

const useAiCoachConversation = (appId?: string): UseAiCoachConversationResult => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { application } = useSelector(getApplicationData);
  const { entries, options, terminal, phase, isLoading, isError, started, lastOptionId, clientAction } = useSelector(
    getAiCoachConversation,
  );

  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const applicationRef = useRef(application);
  applicationRef.current = application;

  const isMountedRef = useRef(false);

  const selectOption = useCallback(
    (optionId: string) => {
      if (!appId || isLoading) return;

      const chosen = options.find((option) => option.id === optionId);
      if (!chosen) return;

      dispatch(addAiCoachUserChoice(chosen.label));
      dispatch(postAiCoachConversation({ appId, optionId }));
    },
    [appId, isLoading, options, dispatch],
  );

  const retry = useCallback(() => {
    if (!appId || isLoading) return;
    dispatch(postAiCoachConversation({ appId, optionId: lastOptionId }));
  }, [appId, isLoading, lastOptionId, dispatch]);

  // Kick off the initial turn only once per conversation. Reopening the modal
  // resumes the in-progress conversation instead of restarting it.
  useEffect(() => {
    if (!appId || started) return;
    dispatch(postAiCoachConversation({ appId }));
  }, [appId, started, dispatch]);

  useEffect(() => {
    if (!clientAction) return undefined;

    const action = clientAction;
    let executed = false;

    const timer = setTimeout(() => {
      executed = true;
      const app = applicationRef.current;

      switch (action) {
        case AiCoachTurnOptionClientAction.Intercom:
          showIntercom();
          break;
        case AiCoachTurnOptionClientAction.StartGoal:
          if (app?.id) dispatch(getAICoachGoals(app.id));

          if (app?.borrowerId) {
            dispatch(getGoalProgress(app.borrowerId));
            dispatch(getNextSteps(app.borrowerId));
          }
          break;
        case AiCoachTurnOptionClientAction.ConsolidateDebt:
          dispatch(closeAIChat());
          navigateRef.current(RoutePath.OfferAvailable, OFFER_AVAILABLE_FROM_DASHBOARD_STATE);
          break;
        default:
          break;
      }

      dispatch(clearAiCoachClientAction());
    }, CLIENT_ACTION_DELAY_MS);

    return () => {
      clearTimeout(timer);

      if (!executed && !isMountedRef.current) {
        dispatch(clearAiCoachClientAction());
      }
    };
  }, [clientAction, dispatch]);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return { entries, options, terminal, loading: isLoading, error: isError, phase, selectOption, retry };
};

export default useAiCoachConversation;
